import { TtsRateValues } from "@shared-enums/accessibility/accessibility.tts.rate.enum";
import type AccessibilityTtsRateEnum from "@shared-enums/accessibility/accessibility.tts.rate.enum";
import type AccessibilityTtsVoiceEnum from "@shared-enums/accessibility/accessibility.tts.voice.enum";
import type TtsEngineInterface from "@shared-interfaces/accessibility/tts.engine.interface";

/**
 * Le moteur qui prononce le texte. Chaque app pose le sien au démarrage : la synthèse du
 * navigateur pour le web, l'admin, le coach et iOS ; celle du système sur Android, où la WebView
 * n'expose pas la première. `shared/` ne pouvant pas importer un plugin Capacitor, le choix se
 * déclare au démarrage plutôt qu'ici — ce fichier ne sait jamais lequel répond.
 *
 * Tant qu'aucun moteur n'est posé, le lecteur se tait au lieu de rompre : une app qui oublie de
 * le déclarer perd la voix, elle ne plante pas.
 */
let engine: TtsEngineInterface | null = null;

const setTtsEngine = (value: TtsEngineInterface): void =>
{
	engine?.stop();
	engine = value;
};

/**
 * Configuration TTS actuelle
 */
let currentRate: keyof typeof AccessibilityTtsRateEnum = "accessibility-tts-rate-normal";

/**
 * Configure la vitesse de lecture
 */
const setTtsRate = (rate: keyof typeof AccessibilityTtsRateEnum): void =>
{
	currentRate = rate;
};

/**
 * Configure le genre de la voix
 */
const setTtsVoiceGender = (gender: keyof typeof AccessibilityTtsVoiceEnum): void =>
{
	engine?.setVoiceGender?.(gender);
};

/**
 * Récupère la valeur numérique du rate actuel
 */
const getCurrentRateValue = (): number =>
{
	return TtsRateValues[currentRate] ?? 1;
};

/**
 * Génère un ID unique pour les éléments TTS
 */
const generateTtsId = (prefix: string = "tts"): string =>
{
	return prefix + "-" + Math.random().toString(36).substring(2, 11);
};

/**
 * Enveloppe les mots dans des spans et retourne le texte nettoyé pour la synthèse vocale.
 * Le texte retourné est synchronisé avec les charIndex des spans.
 * Les spans sont groupés dans un conteneur pour préserver la mise en page flex.
 */
function wrapWordsWithSpans(container: HTMLElement): string
{
	const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null);
	const textNodes: Node[] = [];

	// 1. Collecter tous les TextNodes
	while (walker.nextNode())
	{
		const node = walker.currentNode;

		if (node && node.nodeValue?.trim())
		{
			textNodes.push(node);
		}
	}

	// 2. Transformer chaque TextNode en spans et construire le texte synchronisé
	let charIndex = 0;
	let isFirstWord = true;
	let textForUtterance = "";
	let isFirstTextNode = true;

	for (const node of textNodes)
	{
		const text = node.nodeValue;

		// Nettoyage du texte (® et son entité HTML &reg;)
		const cleanedText = text?.replace(/®|&reg;/gi, "") || "";

		// Ajouter un espace entre les noeuds texte (ex: entre deux <li>)
		if (! isFirstTextNode && textForUtterance.length > 0 && ! textForUtterance.endsWith(" "))
		{
			textForUtterance += " ";
			charIndex += 1;
		}
		isFirstTextNode = false;

		// Création des mots
		const words = cleanedText.match(/\S+|\s+/g); // conserve mots + espaces

		if (! words)
		{
			continue;
		}

		// Créer un conteneur wrapper pour préserver la mise en page (flex, grid, etc.)
		const wrapper = document.createElement("span");
		wrapper.classList.add("tts-words-container");

		for (const word of words)
		{
			const isWhitespace = /^\s+$/.test(word);

			// Ne pas compter les espaces au tout début
			if (isFirstWord && isWhitespace)
			{
				const span = document.createElement("span");
				span.textContent = word;
				span.classList.add("tts-word");
				wrapper.appendChild(span);
				continue;
			}

			if (! isWhitespace)
			{
				isFirstWord = false;
			}

			const span = document.createElement("span");
			span.textContent = word;
			span.dataset.charStart = String(charIndex);
			span.dataset.charEnd = String(charIndex + word.length);
			span.classList.add("tts-word");
			wrapper.appendChild(span);

			// Ajoute au texte pour l'utterance (normalise les espaces multiples en un seul)
			textForUtterance += isWhitespace ? " " : word;
			charIndex += isWhitespace ? 1 : word.length;
		}

		// @ts-ignore
		node.replaceWith(wrapper);
	}

	return textForUtterance.trim();
}

// Workaround Chrome bug : la synthèse vocale s'arrête après ~15s
// On relance périodiquement resume() pour maintenir la lecture

const speak = (originalContent: string | null, selector: string, bubbleSelector: string) =>
{
	const bubble = document.querySelector(bubbleSelector);
	const content = document.querySelector(selector) as HTMLElement;

	if (bubble?.classList.contains("is-playing"))
	{
		engine?.stop();
		bubble?.classList?.remove("is-playing");

		if (content && originalContent)
		{
			content.innerHTML = originalContent;
			content.classList.remove("tts-block");
		}
	}
	else
	{
		if (content)
		{
			originalContent = content.innerHTML;

			// Prépare les spans et récupère le texte synchronisé pour la synthèse
			const textForUtterance = wrapWordsWithSpans(content);

			let boundaryTriggered = false;
			let fallbackTimeout: number;

			// Fonction pour surligner un mot par son charIndex
			const highlightWordAtIndex = (charIndex: number) =>
			{
				const spans = content.querySelectorAll("[data-char-start]");

				for (const span of Array.from(spans))
				{
					const spanEl = span as HTMLElement;
					const charStart = spanEl.dataset.charStart;
					const charEnd = spanEl.dataset.charEnd;

					if (charStart === undefined || charEnd === undefined)
					{
						continue;
					}

					const start = parseInt(charStart, 10);
					const end = parseInt(charEnd, 10);

					if (charIndex >= start && charIndex < end)
					{
						content.querySelectorAll(".tts-highlighted").forEach(s => s.classList.remove("tts-highlighted"));
						span.classList.add("tts-highlighted");
						break;
					}
				}
			};

			const highlightFirstWord = () =>
			{
				boundaryTriggered = true;
				clearTimeout(fallbackTimeout);

				// Trouve et surligne le premier mot (premier span non-whitespace)
				const firstWordSpan = content.querySelector(".tts-word:not(:empty)");
				if (firstWordSpan && firstWordSpan.textContent?.trim())
				{
					firstWordSpan.classList.add("tts-highlighted");
				}
			};

			const onBoundary = (charIndex: number) =>
			{
				boundaryTriggered = true;
				clearTimeout(fallbackTimeout);
				highlightWordAtIndex(charIndex);
			};

			// La lecture finie — ou interrompue — l'élément retrouve son texte, que le balisage
			// du surlignage avait remplacé.
			const restoreContent = () =>
			{
				if (originalContent)
				{
					content.innerHTML = originalContent;
					content.classList.remove("tts-block");
				}

				bubble?.classList?.remove("is-playing");
			};

			bubble?.classList?.add("is-playing");

			engine?.speak(textForUtterance, getCurrentRateValue(), {
				onStart: highlightFirstWord,
				onBoundary: onBoundary,
				onEnd: restoreContent,
			});

			// Fallback si aucun onboundary déclenché
			fallbackTimeout = window.setTimeout(() =>
			{
				if (! boundaryTriggered)
				{
					content.classList.add("tts-block");
				}
			}, 300);

			return originalContent;
		}
	}

	return null;
};

/**
 * Lecture simple d'un texte sans surlignage (pour boutons, labels courts, etc.)
 */
const speakText = (text: string, onEnd?: () => void): void =>
{
	if (! text?.trim())
	{
		return;
	}

	// Nettoie le texte HTML
	const cleanText = text
		.replace(/<[^>]*>/g, "") // Supprime les balises HTML
		.replace(/&nbsp;/g, " ")
		.replace(/&shy;/g, "")
		.replace(/®|&reg;/gi, "")
		.trim();

	if (! cleanText)
	{
		return;
	}

	engine?.speak(cleanText, getCurrentRateValue(), { onEnd: onEnd });
};

/**
 * Arrête toute lecture en cours.
 *
 * Appelé au démarrage de l'app pour appliquer un lecteur vocal éteint : il doit rester sans
 * effet quand rien ne parle, y compris là où aucune synthèse n'existe. Une exception ici
 * interrompait `onMounted` et, avec lui, le chargement de l'adhérent.
 */
const stopSpeaking = (): void =>
{
	engine?.stop();
};

/**
 * Ce qu'il faut faire quand le lecteur vocal s'éteint en pleine phrase.
 *
 * La directive `v-tts` l'installe : elle seule sait rendre à l'élément le texte que le
 * surlignage mot à mot lui a pris. Le store, qui porte l'interrupteur, ne peut pas l'importer
 * sans créer un cycle — d'où ce point de rendez-vous.
 */
let onTtsDisabled: (() => void) | null = null;

const setTtsDisabledHandler = (handler: (() => void) | null): void =>
{
	onTtsDisabled = handler;
};

const stopAllSpeaking = (): void =>
{
	stopSpeaking();
	onTtsDisabled?.();
};

export {
	generateTtsId,
	wrapWordsWithSpans,
	speak,
	speakText,
	stopSpeaking,
	stopAllSpeaking,
	setTtsDisabledHandler,
	setTtsEngine,
	setTtsRate,
	setTtsVoiceGender
};
